import { PortCallStatusEnum } from 'src/enums/stowage/portCallStatusEnum';
import { BlockedTank } from 'src/types/ViewModel/Stowage/BlockedTank';
import { BerthName } from 'src/types/ViewModel/Stowage/Port/BerthName';
import { PortName } from 'src/types/ViewModel/Stowage/Port/PortName';
import { Product } from 'src/types/ViewModel/Stowage/Product/Product';
import { RotationBerthCall } from 'src/types/ViewModel/Stowage/Rotation/RotationBerthCall';
import { RotationCargoOperation } from 'src/types/ViewModel/Stowage/Rotation/RotationCargoOperation';
import { RotationPortCall } from 'src/types/ViewModel/Stowage/Rotation/RotationPortCall';
import { Stowage } from 'src/types/ViewModel/Stowage/Stowage';
import { StowagePlan } from 'src/types/ViewModel/Stowage/StowagePlan';
import { getFormattedCargoNumber, isLoadingOrUnloadingCargoOperation } from './cargoOperationUtils';
import { isNullOrUndefined } from '../objectUtils';
import { AdjustedCargoTemperature } from 'src/types/ViewModel/Stowage/AdjustedCargoTemperature';
import { RotationCargo } from 'src/types/ViewModel/Stowage/Rotation/RotationCargo';
import { Vessel } from 'src/types/ViewModel/Vessel/Vessel';
import { Violation } from 'src/types/ViewModel/Stowage/Violation/Violation';
import { getNominatedWeighAfterOptionAdjustement } from './cargoOptionUtils';
import { createTransitBerthCall } from 'src/factory/Stowage/Rotation/RotationBerthCallFactory';
import { RotationElement } from './stowagePlanComparison/itemFindersUtils';
import { getDefaultCargoTemperature } from './stowageOperation/cargoTemperatureUtils';

export const mapRotation = (
    stowagePlan: StowagePlan,
    rotationPortCallList: RotationPortCall[],
    cargolist: RotationCargo[],
    portNameList: PortName[],
    berthNameList: BerthName[],
    productList: Product[],
    selectedVessel: Vessel,
    violations: Violation[]
): RotationPortCall[] => {
    const filteredCargolist = addProductAndfilterCargoWithoutProduct(cargolist, productList);
    addCargoAndFilterIncompleteCargoOperations(rotationPortCallList, filteredCargolist);
    addNamesToRotation(rotationPortCallList, portNameList, berthNameList);
    addTransitBerthCalls(rotationPortCallList);

    stowagePlan.berthCalls = rotationPortCallList.flatMap((pc) => getBerthCalls(pc));
    stowagePlan.cargoOperations = stowagePlan.berthCalls.flatMap((bc) => bc.cargoOperations);
    stowagePlan.cargoes = filteredCargolist;

    rotationPortCallList.forEach((pc) => {
        getBerthCalls(pc).forEach((bc) => {
            bc.portCall = pc;
            bc.cargoOperations.forEach((op) => {
                op.berthCall = bc;
            });
        });
    });

    updateCargoOperations(rotationPortCallList, stowagePlan.cargoOperations);

    stowagePlan.rotation = rotationPortCallList;
    stowagePlan.transitOperations = [];
    stowagePlan.transitStowageItems = [];

    stowagePlan.stowageItems.forEach((si) => {
        addReferencesToStowageItem(si, stowagePlan.cargoOperations, selectedVessel);
    });
    stowagePlan.stowageItems = stowagePlan.stowageItems.filter((si) => si.cargoOperation);

    stowagePlan.adjustedCargoTemperatures.forEach((act) => {
        addReferencesToAdjustedCargoTemperatures(act, stowagePlan.cargoes, selectedVessel);
    });
    stowagePlan.blockedTanks.forEach((bt) => {
        addReferencesToBlockedTanks(bt, rotationPortCallList, selectedVessel);
    });

    if (violations) {
        violations.forEach((v) => {
            addReferencesToViolation(v, selectedVessel, stowagePlan);
        });
    }
    return rotationPortCallList;
};

export const getBerthCalls = (portCall: RotationPortCall): RotationBerthCall[] => {
    return portCall?.berthCalls?.filter((b) => b.berthId !== -1) ?? [];
};

export const getFirstBerthCallInPortCall = (portCall: RotationPortCall): RotationBerthCall | null => {
    const berthCalls = getBerthCalls(portCall);
    return berthCalls.length > 0 ? berthCalls[0] : null;
};

export const getLastBerthCallInPortCall = (portCall: RotationPortCall): RotationBerthCall | null => {
    const berthCalls = getBerthCalls(portCall);
    return berthCalls.length > 0 ? berthCalls[berthCalls.length - 1] : null;
};

export const getPreviousPortCall = (stowagePlan: StowagePlan, portCall: RotationPortCall | null): RotationPortCall | null => {
    const index = stowagePlan.rotation.findIndex((pc) => pc.portCallGuid === portCall?.portCallGuid);
    if (index <= 0) {
        return null;
    }
    return stowagePlan.rotation[index - 1];
};

export const getPreviousBerthCall = (stowagePlan: StowagePlan, berthCall: RotationBerthCall): RotationBerthCall | null => {
    for (let i = 0; i < stowagePlan.rotation.length; i++) {
        const pc = stowagePlan.rotation[i];
        const berthCalls = getBerthCalls(pc);
        for (let j = 0; j < berthCalls.length; j++) {
            const bc = berthCalls[j];
            if (bc.berthCallGuid === berthCall.berthCallGuid) {
                if (i === 0 && j === 0) {
                    return null;
                } else if (j === 0) {
                    const previousPortCallBerthCalls = getBerthCalls(stowagePlan.rotation[i - 1]);
                    return previousPortCallBerthCalls[previousPortCallBerthCalls.length - 1];
                } else {
                    return pc.berthCalls[j - 1];
                }
            }
        }
    }
    return null;
};

export const getLastBerthCallFromPrecedingPortCalls = (stowagePlan: StowagePlan, currentPortCall: RotationPortCall): RotationBerthCall | null => {
    let previousPortCall = getPreviousPortCall(stowagePlan, currentPortCall);

    while (previousPortCall) {
        const lastBerthCall = getLastBerthCallInPortCall(previousPortCall);
        if (lastBerthCall) {
            return lastBerthCall;
        }
        previousPortCall = getPreviousPortCall(stowagePlan, previousPortCall);
    }

    return null;
};

export const getBerthCallForSelection = (
    stowagePlan: StowagePlan,
    selectedPortCall: RotationPortCall,
    selectedBerthCall: RotationBerthCall
): RotationBerthCall => {
    return (
        selectedBerthCall ??
        getLastBerthCallInPortCall(selectedPortCall) ??
        getLastBerthCallInPortCall(getPreviousPortCall(stowagePlan, selectedPortCall)) ??
        getLastBerthCallFromPrecedingPortCalls(stowagePlan, selectedPortCall)
    );
};

export const getCargoOperationForCargoAndOperationType = (
    stowagePlan: StowagePlan,
    cargo: RotationCargo,
    operationType: string,
    toBerthCall?: RotationBerthCall
): RotationCargoOperation[] => {
    let partialBerthCalls = stowagePlan.berthCalls ?? [];
    if (toBerthCall) {
        const toBerthCallIndex = partialBerthCalls.findIndex((bc) => bc?.berthCallGuid === toBerthCall.berthCallGuid);
        if (toBerthCallIndex !== -1) {
            partialBerthCalls = partialBerthCalls.slice(0, toBerthCallIndex + 1);
        }
    }

    return partialBerthCalls
        .filter((bc) => bc?.cargoOperations)
        .flatMap((bc) => bc.cargoOperations ?? [])
        .filter((co) => co?.cargoGuid === cargo.cargoGuid && co?.operationType === operationType);
};

export const getCargoOperationForCargo = (stowagePlan: StowagePlan, cargo: RotationCargo): RotationCargoOperation[] => {
    return stowagePlan.rotation
        .flatMap((pc) => pc.berthCalls)
        .flatMap((bc) => bc.cargoOperations)
        .filter((co) => co.cargoGuid === cargo.cargoGuid);
};

export const getCargoOperationsAfter = (stowagePlan: StowagePlan, cargoOperation: RotationCargoOperation): RotationCargoOperation[] => {
    let cargoOperations = getCargoOperationForCargo(stowagePlan, cargoOperation.cargo);

    const cargoOperationIndex = cargoOperations.findIndex((op) => op.cargoOperationGuid === cargoOperation.cargoOperationGuid);
    if (cargoOperationIndex === -1 || cargoOperationIndex === cargoOperations.length - 1) {
        cargoOperations = [];
    }
    return cargoOperations.slice(cargoOperationIndex + 1);
};

export const getCurrentPortCall = (stowagePlan: StowagePlan): RotationPortCall => {
    // First, try to find a port call with "Arrived" status (vessel is currently at port)
    const arrivedPortCall = stowagePlan.rotation.find((pc) => pc.portCallStatus === PortCallStatusEnum.Arrived);
    if (arrivedPortCall) return arrivedPortCall;

    // Next, try to find the most recent departed port call (vessel has departed and is en route)
    const departedPortCall = stowagePlan.rotation
        .slice()
        .reverse()
        .find((pc) => pc.portCallStatus === PortCallStatusEnum.Departed);
    if (departedPortCall) return departedPortCall;

    // If no arrived or departed port calls exist (vessel not delivered yet),
    // find the first scheduled port call to auto-select
    const scheduledPortCall = stowagePlan.rotation.find((pc) => pc.portCallStatus === PortCallStatusEnum.Planned);
    if (scheduledPortCall) return scheduledPortCall;

    // Fallback to the first port call in the rotation if none of the above conditions are met
    return stowagePlan.rotation[0];
};

export const getDefaultStowageRotationTree = (stowagePlan: StowagePlan): RotationPortCall[] => {
    // TODO: NAMING-CONVENTION - Variable name starts with underscore (_currentPortCall, _currentVoyage, _previousVoyage) - underscore prefix is typically reserved for private/internal variables. Should use standard naming: currentPortCall, currentVoyage, previousVoyage.
    const _currentPortCall = getCurrentPortCall(stowagePlan);

    if (!_currentPortCall) return [];
    const _currentVoyage = _currentPortCall.voyageNumber;
    const _previousVoyage = Number(_currentVoyage) - 1;
    return stowagePlan.rotation.filter((p) => Number(p.voyageNumber) >= _previousVoyage);
};

export const isManualCall = (rotationElement: RotationElement) => rotationElement.dataSource === 'OrcaWeb';

export const getVoyageNumberString = (portCall: RotationPortCall): string => {
    return portCall.turnaroundVoyageNumber ? `${portCall.voyageNumber} / ${portCall.turnaroundVoyageNumber}` : portCall.voyageNumber.toString();
};

/**
 * Validates that a turnaround port call has different voyage numbers.
 * A turnaround should never have the same voyageNumber and turnaroundVoyageNumber
 * (e.g., "202510 / 202510 - Ulsan" is invalid)
 */
export const isValidTurnaroundVoyageNumber = (portCall: RotationPortCall): boolean => {
    // If there's no turnaround voyage number, it's valid (not a turnaround)
    if (!portCall.turnaroundVoyageNumber) {
        return true;
    }
    // Turnaround voyage number must be different from the main voyage number
    return portCall.voyageNumber !== portCall.turnaroundVoyageNumber;
};

/**
 * Validates all port calls in a rotation have valid turnaround voyage numbers.
 * Returns an array of invalid port calls.
 */
export const getInvalidTurnaroundPortCalls = (rotation: RotationPortCall[]): RotationPortCall[] => {
    return rotation.filter((portCall) => !isValidTurnaroundVoyageNumber(portCall));
};

export const isPortCallAfterFirstVoyageOrcaWebStowageImosSync = (portCall: RotationPortCall, firstVoyageOrcaWebStowageImosSync: number) => {
    return +portCall.voyageNumber >= firstVoyageOrcaWebStowageImosSync || +portCall.turnaroundVoyageNumber >= firstVoyageOrcaWebStowageImosSync;
};
export const isCargoAfterFirstVoyageOrcaWebStowageImosSync = (cargo: RotationCargo, firstVoyageOrcaWebStowageImosSync: number) => {
    return +cargo.voyageNumber >= firstVoyageOrcaWebStowageImosSync;
};

const findBerthCallIndices = (
    berthCalls: RotationBerthCall[],
    berthCall1: RotationBerthCall,
    berthCall2: RotationBerthCall
): { index1: number; index2: number } | null => {
    if (!berthCalls || !berthCall1 || !berthCall2) return null;

    const index1 = berthCalls.findIndex((bc) => bc.berthCallGuid === berthCall1.berthCallGuid);
    const index2 = berthCalls.findIndex((bc) => bc.berthCallGuid === berthCall2.berthCallGuid);

    if (index1 === -1 || index2 === -1) return null;

    return { index1, index2 };
};

export const isBefore = (berthCalls: RotationBerthCall[], berthCall1: RotationBerthCall, berthCall2: RotationBerthCall): boolean => {
    const indices = findBerthCallIndices(berthCalls, berthCall1, berthCall2);
    return indices ? indices.index1 < indices.index2 : false;
};

export const isBeforeOrEqual = (berthCalls: RotationBerthCall[], berthCall1: RotationBerthCall, berthCall2: RotationBerthCall): boolean => {
    const indices = findBerthCallIndices(berthCalls, berthCall1, berthCall2);
    return indices ? indices.index1 <= indices.index2 : false;
};

export const addProductAndfilterCargoWithoutProduct = (rotationCargoList: RotationCargo[], productList: Product[]): RotationCargo[] => {
    return rotationCargoList.reduce((acc, cargo) => {
        const product = productList.find((p) => p.id === cargo.productId);
        if (product) {
            cargo.product = product;
            cargo.temperature = getDefaultCargoTemperature(cargo);

            acc.push(cargo);
        } else {
            console.log(
                `Removing cargo without product: IMOS Cargo Id: ${cargo.imosCargoId}, Cargo Number: ${getFormattedCargoNumber(
                    cargo.voyageNumber?.toString(),
                    cargo.cargoNumber,
                    false
                )}, Product Id: ${cargo.productId}`
            );
        }
        return acc;
    }, []);
};

export const addCargoAndFilterIncompleteCargoOperations = (rotationPortCallList: RotationPortCall[], rotationCargoList: RotationCargo[]) => {
    rotationPortCallList.forEach((pc) => {
        pc.berthCalls?.forEach((bc) => {
            bc.cargoOperations = addCargoAndFilterCargoOperationsWithoutCargo(rotationCargoList, bc);
        });
    });
};

const addTransitBerthCalls = (rotationPortCallList: RotationPortCall[]) => {
    for (const portCall of rotationPortCallList) {
        if (!portCall.berthCalls.some((b) => b.berthId === -1)) {
            const transitBerthCall = createTransitBerthCall(portCall);
            portCall.berthCalls.push(transitBerthCall);
        }
    }
};

export const addNamesToRotation = (rotationPortCallList: RotationPortCall[], portNameList: PortName[], berthNameList: BerthName[]) => {
    rotationPortCallList.forEach((pc) => {
        if (typeof pc.portId === 'string') pc.portId = Number(pc.portId);
        pc.portDisplayName = portNameList.find((p) => p.portId === pc.portId)?.name ?? `Port ${pc.portId} not found`;

        pc.berthCalls?.forEach((bc) => {
            if (typeof bc.berthId === 'string') bc.berthId = Number(bc.berthId);
            bc.berthName =
                bc.berthId === 0 ? 'TBN' : berthNameList.find((b) => b.berthId === bc.berthId)?.longName ?? `Berth ${bc.berthId} not found`;
        });
    });
};

const addCargoAndFilterCargoOperationsWithoutCargo = (rotationCargoList: RotationCargo[], berthCall: RotationBerthCall): RotationCargoOperation[] => {
    return berthCall.cargoOperations.reduce((acc, op) => {
        const cargo = rotationCargoList.find((c) => c.cargoGuid === op.cargoGuid || ((c?.imosCargoId ?? 0) > 0 && c.imosCargoId === op.imosCargoId));
        if (cargo) {
            op.cargo = cargo;
            if (!op.cargoGuid || op.cargoGuid !== cargo.cargoGuid) {
                op.cargoGuid = cargo.cargoGuid;
            }
            if (cargo.imosCargoId && op.imosCargoId !== cargo.imosCargoId) {
                op.imosCargoId = cargo.imosCargoId;
            }
            acc.push(op);
        }
        return acc;
    }, []);
};

export const isVoyageClosedOrCompleted = (voyageStatus: string) => voyageStatus === 'Closed' || voyageStatus === 'Completed';

const addReferencesToViolation = (violation: Violation, vessel: Vessel, stowagePlan: StowagePlan): void => {
    violation.portCall = stowagePlan.rotation.find((p) => p.portCallGuid === violation.portCallGuid);
    violation.berthCall = stowagePlan.berthCalls.find((berthCall) => berthCall.berthCallGuid === violation.berthCallGuid);
    violation.cargoOperation = stowagePlan.cargoOperations.find(
        (cargoOperation) => cargoOperation.cargoOperationGuid === violation.cargoOperationGuid
    );
    violation.cargo = stowagePlan.cargoes.find((cargo) => cargo.cargoGuid === violation.cargoGuid);
    violation.tank = vessel.cargoTanks.find((tank) => tank.tankId === violation.tankId);
    violation.adjacentTank = vessel.cargoTanks.find((tank) => tank.tankId === violation.adjacentTankId);
};

const addReferencesToStowageItem = (stowageItem: Stowage, cargoOperations: RotationCargoOperation[], selectedVessel: Vessel) => {
    if (isNullOrUndefined(stowageItem?.tankId)) return;
    stowageItem.tank = selectedVessel.cargoTanks.find((tank) => tank.tankId === stowageItem.tankId);
    stowageItem.cargoOperation = cargoOperations?.find((co) => co?.cargoOperationGuid === stowageItem?.cargoOperationGuid);
};

const addReferencesToBlockedTanks = (blockedTank: BlockedTank, rotation: RotationPortCall[], selectedVessel: Vessel) => {
    if (!blockedTank) return;
    blockedTank.tank = selectedVessel.cargoTanks.find((tank) => tank.tankId === blockedTank.tankId);
    blockedTank.fromPortCall = rotation.find((p) => p.portCallGuid === blockedTank.fromPortCallGuid);
    blockedTank.fromBerthCall = rotation.flatMap((p) => p.berthCalls).find((berthCall) => berthCall.berthCallGuid === blockedTank.fromBerthCallGuid);

    if (!isNullOrUndefined(blockedTank.toPortCallGuid)) {
        blockedTank.toPortCall = rotation.find((p) => p.portCallGuid === blockedTank.toPortCallGuid);
    }
    if (!isNullOrUndefined(blockedTank.toBerthCallGuid)) {
        blockedTank.toBerthCall = rotation.flatMap((p) => p.berthCalls).find((berthCall) => berthCall.berthCallGuid === blockedTank.toBerthCallGuid);
    }
};

const addReferencesToAdjustedCargoTemperatures = (
    adjustedCargoTemperature: AdjustedCargoTemperature,
    cargoes: RotationCargo[],
    selectedVessel: Vessel
) => {
    if (isNullOrUndefined(adjustedCargoTemperature?.tankId)) return;
    adjustedCargoTemperature.tank = selectedVessel.cargoTanks.find((tank) => tank.tankId === adjustedCargoTemperature.tankId);
    adjustedCargoTemperature.cargo = cargoes.find((cargo) => cargo.cargoGuid === adjustedCargoTemperature.cargoGuid);
};

const getPortNames = (cargo: RotationCargo, operationType: string, allCargoOperationsList: RotationCargoOperation[]) => {
    return allCargoOperationsList
        .filter((c) => c.cargoGuid === cargo.cargoGuid && c.operationType === operationType)
        .map((g) => ({
            portId: g.berthCall.portCall.portId,
            name: g.berthCall.portCall.portDisplayName,
        }));
};

export const updateCargoOperations = (rotationPortCallList: RotationPortCall[], cargoOperations: RotationCargoOperation[]) => {
    rotationPortCallList.forEach((pc) => {
        pc.berthCalls?.forEach((bc) => {
            const updatedCargoOperations = bc.cargoOperations.reduce((acc, op) => {
                op.cargo.loadingPortNames = getPortNames(op.cargo, 'Loading', cargoOperations);
                op.cargo.unloadingPortNames = getPortNames(op.cargo, 'Unloading', cargoOperations);
                // Only set nominatedWeight if it's not already provided (e.g., from DB/stowage changes)
                if (isLoadingOrUnloadingCargoOperation(op) && op.nominatedWeight == null) {
                    op.nominatedWeight = getNominatedWeighAfterOptionAdjustement(op.contractWeight, op.cargo);
                }

                acc.push(op);
                return acc;
            }, []);

            bc.cargoOperations = updatedCargoOperations;
        });
    });
};

export const getFirstImosCargoOperationForCargo = (rotation: RotationPortCall[], cargo: RotationCargo): RotationCargoOperation | null => {
    for (const portCall of rotation) {
        for (const berthCall of portCall.berthCalls) {
            const cargoOperation = berthCall.cargoOperations.find((op) => op.imosCargoId === cargo.imosCargoId);
            if (cargoOperation) {
                return cargoOperation;
            }
        }
    }
    return null;
};

export const isManualBerthCall = (berthCall: RotationBerthCall): boolean => {
    return berthCall.dataSource === 'OrcaWeb';
};

export const isManualPortCall = (portCall: RotationPortCall): boolean => {
    return portCall.dataSource === 'OrcaWeb';
};

/**
 * Reconciles manual sequence change markers (hasChangedSequence) against the latest incoming IMOS data.
 *
 * Business rule (ADO #31757): If a port call / berth call / cargo operation was manually reordered locally
 * and later an IMOS sync arrives that already reflects this new order, the item should no longer display
 * as having a changed sequence. In that case, clear the hasChangedSequence flag even if the original
 * sequence number stored in the flag differs from the current one (because the new order is now the
 * "official" IMOS order).
 *
 * We consider a sequence reconciled when:
 *  - The item has hasChangedSequence set (object or truthy)
 *  - There exists an incoming IMOS counterpart (matched by IMOS id) whose sequence number equals the current one
 *  - (For cargo operations) parent berth call match is also confirmed through IMOS ids / guids (optional safeguard)
 */
export const reconcileSequenceChangeFlags = (existingRotation: RotationPortCall[], incomingImosRotation: RotationPortCall[]): void => {
    if (!existingRotation?.length || !incomingImosRotation?.length) return;

    // Build quick lookup maps by IMOS ids
    const imosPortCallMap = new Map<number, RotationPortCall>();
    const imosBerthCallMap = new Map<number, { portCallGuid: string; berthCall: RotationBerthCall }>();
    const imosCargoOpMap = new Map<number, { berthCallGuid?: string; cargoOperation: RotationCargoOperation }>();

    incomingImosRotation.forEach((pc) => {
        if (pc.imosPortCallId) imosPortCallMap.set(pc.imosPortCallId, pc);
        pc.berthCalls?.forEach((bc) => {
            if (bc.imosBerthCallId) imosBerthCallMap.set(bc.imosBerthCallId, { portCallGuid: pc.portCallGuid, berthCall: bc });
            bc.cargoOperations?.forEach((co) => {
                if (co.imosCargoOperationId) imosCargoOpMap.set(co.imosCargoOperationId, { berthCallGuid: bc.berthCallGuid, cargoOperation: co });
            });
        });
    });

    const clearIfReconciled = (item: any, currentSeq: number, imosSeq: number | undefined) => {
        if (!item?.hasChangedSequence) return; // nothing to do
        if (imosSeq == null) return; // no IMOS reference -> keep manual flag
        if (currentSeq === imosSeq) {
            // Sequence now matches IMOS -> clear flag
            item.hasChangedSequence = null;
        }
    };

    existingRotation.forEach((pc) => {
        if (pc.imosPortCallId) {
            const imosPc = imosPortCallMap.get(pc.imosPortCallId);
            clearIfReconciled(pc, pc.portOrderNumber, imosPc?.portOrderNumber);
        }
        pc.berthCalls?.forEach((bc) => {
            if (bc.imosBerthCallId) {
                const imosBcInfo = imosBerthCallMap.get(bc.imosBerthCallId);
                clearIfReconciled(bc, bc.berthSequenceNumber, imosBcInfo?.berthCall.berthSequenceNumber);
            }
            bc.cargoOperations?.forEach((co) => {
                if (co.imosCargoOperationId) {
                    const imosCoInfo = imosCargoOpMap.get(co.imosCargoOperationId);
                    // Optional parent berth validation: only clear if parent also corresponds
                    const parentMatches = !imosCoInfo?.berthCallGuid || imosCoInfo.berthCallGuid === co.berthCallGuid;
                    if (parentMatches) {
                        clearIfReconciled(co, co.cargoOperationSequenceNumber, imosCoInfo?.cargoOperation.cargoOperationSequenceNumber);
                    }
                }
            });
        });
    });
};

export const isValidPortCall = (portCall: RotationPortCall): boolean => {
    // TODO: VALIDATION - No null/undefined check for portCall parameter. Should validate portCall exists before accessing properties.
    return portCall.imosPortFunction === 'M' || isManualPortCall(portCall);
};
